import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, BarChart3, Target, Settings2, Check, X, ChevronDown, ChevronUp, Cookie, FileText, ArrowLeft, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Switch } from './ui/switch';
import { Button } from './ui/button';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { useTranslations } from '../context/TranslationsContext';

const API = process.env.REACT_APP_BACKEND_URL;
const CONSENT_COOKIE_NAME = 'avepark_cookie_consent';
const CONSENT_VERSION = '2.0';

const defaultConsent = {
  necessary: true,
  analytics: false,
  marketing: false,
  functional: false,
};

// Initialize GTM with default denied state
const initializeGTM = (gtmId) => {
  if (!gtmId || typeof window === 'undefined') return;
  
  window.dataLayer = window.dataLayer || [];
  
  window.gtag = function() { window.dataLayer.push(arguments); };
  window.gtag('consent', 'default', {
    'ad_storage': 'denied',
    'ad_user_data': 'denied',
    'ad_personalization': 'denied',
    'analytics_storage': 'denied',
    'functionality_storage': 'denied',
    'personalization_storage': 'denied',
    'security_storage': 'granted',
    'wait_for_update': 500,
  });
  
  if (!document.getElementById('gtm-script')) {
    const script = document.createElement('script');
    script.id = 'gtm-script';
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
    document.head.appendChild(script);
    
    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js'
    });
  }
};

// Update consent in GTM
const updateGTMConsent = (consent) => {
  if (typeof window === 'undefined' || !window.gtag) return;
  
  window.gtag('consent', 'update', {
    'ad_storage': consent.marketing ? 'granted' : 'denied',
    'ad_user_data': consent.marketing ? 'granted' : 'denied',
    'ad_personalization': consent.marketing ? 'granted' : 'denied',
    'analytics_storage': consent.analytics ? 'granted' : 'denied',
    'functionality_storage': consent.functional ? 'granted' : 'denied',
    'personalization_storage': consent.functional ? 'granted' : 'denied',
    'security_storage': 'granted',
  });
  
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'cookie_consent_update',
    cookie_consent: consent
  });
};

const saveConsent = (consent) => {
  const consentData = {
    ...consent,
    version: CONSENT_VERSION,
    timestamp: new Date().toISOString(),
  };
  
  localStorage.setItem(CONSENT_COOKIE_NAME, JSON.stringify(consentData));
  
  const expires = new Date();
  expires.setFullYear(expires.getFullYear() + 1);
  document.cookie = `${CONSENT_COOKIE_NAME}=${encodeURIComponent(JSON.stringify(consentData))}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
};

const loadConsent = () => {
  try {
    const stored = localStorage.getItem(CONSENT_COOKIE_NAME);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed.version === CONSENT_VERSION) {
        return parsed;
      }
    }
  } catch (e) {
    console.error('Error loading consent:', e);
  }
  return null;
};

const CookieConsent = () => {
  const isDigitalSignage = typeof window !== 'undefined' && (window.location.pathname.startsWith('/ds/') || window.location.pathname.startsWith('/tablet'));

  const { i18n } = useTranslation();
  const { td } = useTranslations();
  
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [activeDocument, setActiveDocument] = useState(null); // 'privacy' or 'terms'
  const [documentContent, setDocumentContent] = useState(null);
  const [loadingDocument, setLoadingDocument] = useState(false);
  const [consent, setConsent] = useState(defaultConsent);
  const [isInitialized, setIsInitialized] = useState(false);
  const [settings, setSettings] = useState({
    enabled: true,
    gtmId: 'GTM-N2B4HCC',
    privacyPolicySlug: 'polityka-prywatnosci',
    termsOfServiceSlug: 'regulamin'
  });
  
  const lang = i18n.language?.split('-')[0] || 'pl';
  
  // Fetch cookie consent settings from backend
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/api/settings/cookie-consent`);
        if (response.data) {
          setSettings(prev => ({ ...prev, ...response.data }));
        }
      } catch (error) {
        console.log('Using default cookie consent settings');
      }
    };
    fetchSettings();
  }, []);
  
  useEffect(() => {
    if (!settings.enabled) {
      setIsInitialized(true);
      return;
    }
    
    if (settings.gtmId) {
      initializeGTM(settings.gtmId);
    }
    
    const savedConsent = loadConsent();
    if (savedConsent) {
      setConsent(savedConsent);
      updateGTMConsent(savedConsent);
      setIsVisible(false);
    } else {
      setIsVisible(true);
    }
    setIsInitialized(true);
  }, [settings]);
  
  // Fetch document (privacy policy or terms of service)
  const fetchDocument = async (type) => {
    const slug = type === 'privacy' ? settings.privacyPolicySlug : settings.termsOfServiceSlug;
    if (!slug) return;
    
    setLoadingDocument(true);
    try {
      const response = await axios.get(`${API}/api/documents/by-slug/${slug}`);
      if (response.data) {
        const content = response.data.content?.[lang] || response.data.content?.pl || response.data.content;
        const title = response.data.title?.[lang] || response.data.title?.pl || response.data.title;
        setDocumentContent({ title, content });
        setActiveDocument(type);
      }
    } catch (error) {
      console.error('Failed to load document:', error);
      window.open(`/${lang}/dokument/${slug}`, '_blank');
    } finally {
      setLoadingDocument(false);
    }
  };
  
  const handleAcceptAll = () => {
    const newConsent = { necessary: true, analytics: true, marketing: true, functional: true };
    setConsent(newConsent);
    saveConsent(newConsent);
    updateGTMConsent(newConsent);
    setIsVisible(false);
  };
  
  const handleRejectOptional = () => {
    const newConsent = { necessary: true, analytics: false, marketing: false, functional: false };
    setConsent(newConsent);
    saveConsent(newConsent);
    updateGTMConsent(newConsent);
    setIsVisible(false);
  };
  
  const handleSavePreferences = () => {
    saveConsent(consent);
    updateGTMConsent(consent);
    setIsVisible(false);
  };
  
  const toggleCategory = (category) => {
    if (category === 'necessary') return;
    setConsent(prev => ({ ...prev, [category]: !prev[category] }));
  };
  
  const closeDocument = () => {
    setActiveDocument(null);
    setDocumentContent(null);
  };
  
  useEffect(() => {
    window.openCookieConsent = () => setIsVisible(true);
    return () => { delete window.openCookieConsent; };
  }, []);
  
  if (!isInitialized || !settings.enabled || isDigitalSignage) return null;
  
  const categories = [
    { key: 'necessary', icon: Shield, locked: true },
    { key: 'analytics', icon: BarChart3, locked: false },
    { key: 'marketing', icon: Target, locked: false },
    { key: 'functional', icon: Settings2, locked: false },
  ];
  
  return (
    <AnimatePresence>
      {isVisible && (
        <>
          {/* Blur overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-md z-[9998]"
            data-testid="cookie-consent-overlay"
          />
          
          {/* Cookie consent modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
            data-testid="cookie-consent-modal"
          >
            <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800 border border-white/10 rounded-2xl shadow-2xl">
              
              {/* Document View (Privacy Policy or Terms) */}
              {activeDocument ? (
                <div className="p-6">
                  <button
                    onClick={closeDocument}
                    className="flex items-center gap-2 text-primary hover:text-primary/80 mb-4 transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    {td('cookies.backToSettings', 'Wróć do ustawień')}
                  </button>
                  
                  <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    {documentContent?.title}
                  </h2>
                  
                  <div className="prose prose-invert prose-sm max-w-none max-h-[60vh] overflow-y-auto pr-2
                    prose-headings:text-white prose-headings:font-bold
                    prose-h1:text-xl prose-h1:mb-4 prose-h1:mt-6
                    prose-h2:text-lg prose-h2:mb-3 prose-h2:mt-5
                    prose-h3:text-base prose-h3:mb-2 prose-h3:mt-4
                    prose-p:text-gray-300 prose-p:mb-3 prose-p:leading-relaxed
                    prose-li:text-gray-300 prose-li:mb-1
                    prose-ul:mb-4 prose-ol:mb-4
                    prose-strong:text-white prose-strong:font-semibold
                    prose-a:text-primary prose-a:no-underline hover:prose-a:underline"
                  >
                    <ReactMarkdown>{documentContent?.content || ''}</ReactMarkdown>
                  </div>
                  
                  <div className="mt-6 pt-4 border-t border-white/10">
                    <Button
                      onClick={closeDocument}
                      className="w-full bg-primary hover:bg-primary/90"
                    >
                      {td('cookies.backToSettings', 'Wróć do ustawień')}
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  {/* Header */}
                  <div className="p-6 pb-4 border-b border-white/10">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
                        <Cookie className="w-6 h-6 text-primary" />
                      </div>
                      <h2 className="text-xl font-bold text-white">
                        {td('cookies.title', 'Szanujemy Twoją prywatność')}
                      </h2>
                    </div>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      {td('cookies.description', 'Używamy plików cookie i podobnych technologii, aby zapewnić najlepsze doświadczenia na naszej stronie.')}
                    </p>
                  </div>
                  
                  {/* Quick actions */}
                  <div className="p-6 pb-4 flex flex-wrap gap-3">
                    <Button
                      onClick={handleAcceptAll}
                      className="flex-1 min-w-[140px] bg-primary hover:bg-primary/90 text-white font-medium"
                      data-testid="cookie-accept-all"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      {td('cookies.acceptAll', 'Akceptuj wszystkie')}
                    </Button>
                    <Button
                      onClick={handleRejectOptional}
                      variant="outline"
                      className="flex-1 min-w-[140px] border-white/20 text-white hover:bg-white/10"
                      data-testid="cookie-reject-optional"
                    >
                      <X className="w-4 h-4 mr-2" />
                      {td('cookies.rejectOptional', 'Tylko niezbędne')}
                    </Button>
                    <Button
                      onClick={() => setShowDetails(!showDetails)}
                      variant="ghost"
                      className="flex-1 min-w-[140px] text-gray-400 hover:text-white hover:bg-white/10"
                      data-testid="cookie-customize"
                    >
                      {showDetails ? <ChevronUp className="w-4 h-4 mr-2" /> : <ChevronDown className="w-4 h-4 mr-2" />}
                      {td('cookies.customize', 'Dostosuj')}
                    </Button>
                  </div>
                  
                  {/* Detailed settings */}
                  <AnimatePresence>
                    {showDetails && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="px-6 pb-4">
                          {categories.map(({ key, icon: Icon, locked }, index) => (
                            <div
                              key={key}
                              className={`p-4 rounded-xl border transition-colors ${index > 0 ? 'mt-3' : ''} ${
                                consent[key]
                                  ? 'bg-primary/10 border-primary/30'
                                  : 'bg-white/5 border-white/10'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                    consent[key] ? 'bg-primary/20' : 'bg-white/10'
                                  }`}>
                                    <Icon className={`w-5 h-5 ${consent[key] ? 'text-primary' : 'text-gray-400'}`} />
                                  </div>
                                  <div>
                                    <h3 className="font-medium text-white">
                                      {td(`cookies.${key}.title`, key)}
                                    </h3>
                                    <p className="text-xs text-gray-400">
                                      {td(`cookies.${key}.description`, '')}
                                    </p>
                                    {locked && (
                                      <span className="text-xs text-primary">
                                        {td('cookies.alwaysOn', 'Zawsze włączone')}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <Switch
                                  checked={consent[key]}
                                  onCheckedChange={() => toggleCategory(key)}
                                  disabled={locked}
                                  className={locked ? 'opacity-50' : ''}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        <div className="px-6 pb-4">
                          <Button
                            onClick={handleSavePreferences}
                            className="w-full bg-primary hover:bg-primary/90 text-white font-medium"
                            data-testid="cookie-save-preferences"
                          >
                            <Check className="w-4 h-4 mr-2" />
                            {td('cookies.savePreferences', 'Zapisz preferencje')}
                          </Button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                  
                  {/* Footer with document links */}
                  <div className="px-6 pb-6 pt-2 border-t border-white/10 flex flex-wrap gap-4">
                    <button
                      onClick={() => fetchDocument('privacy')}
                      disabled={loadingDocument}
                      className="text-sm text-primary hover:text-primary/80 transition-colors flex items-center gap-2"
                    >
                      {loadingDocument && activeDocument === 'privacy' ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <FileText className="w-4 h-4" />
                      )}
                      {td('cookies.privacyPolicy', 'Polityka prywatności')}
                    </button>
                    
                    {settings.termsOfServiceSlug && (
                      <button
                        onClick={() => fetchDocument('terms')}
                        disabled={loadingDocument}
                        className="text-sm text-primary hover:text-primary/80 transition-colors flex items-center gap-2"
                      >
                        {loadingDocument && activeDocument === 'terms' ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <FileText className="w-4 h-4" />
                        )}
                        {td('cookies.termsOfService', 'Regulamin')}
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
